import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Phone, Mail, StickyNote, Building2, Briefcase,
  AlertTriangle, Clock, Send, FileText,
} from 'lucide-react';
import { contactsService } from 'api/services/contacts.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
// The Calls tab renders the SHIPPED call component rather than the generic
// timeline. This page's timeline showed a "transcript" badge that was a bare
// <span> — indigo, so it read as a link, and did nothing when clicked. A
// working viewer (open the call, fetch the transcript, play the recording)
// already existed in ContactCalls; the page simply never used it.
import ContactCalls from 'components/Contacts/ContactCalls';

/**
 * One person, and everything we have said to them.
 *
 * Tabs rather than a merged feed because the channels cost wildly different
 * amounts: WhatsApp is one indexed query, calls cross two switches over SSH
 * tunnels. Merging would make opening any profile pay the slowest one.
 */

type TabId = 'whatsapp' | 'calls' | 'email' | 'notes' | 'sms' | 'rcs';

const TABS: { id: TabId; label: string; icon: React.ReactNode; soon?: boolean }[] = [
  { id: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'calls', label: 'Calls', icon: <Phone className="w-4 h-4" /> },
  { id: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
  { id: 'notes', label: 'Remarks', icon: <StickyNote className="w-4 h-4" /> },
  { id: 'sms', label: 'SMS', icon: <Send className="w-4 h-4" />, soon: true },
  { id: 'rcs', label: 'RCS', icon: <Send className="w-4 h-4" />, soon: true },
];

const when = (v: any): string => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const ContactProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<any>(null);
  const [tab, setTab] = useState<TabId>('whatsapp');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const r = await contactsService.getContact(id);
      if (r.success) setContact(r.data); else setError(r.error || 'Could not load this contact');
      setLoading(false);
    })();
  }, [id]);

  const loadTab = useCallback(async (t: TabId) => {
    if (!id) return;
    setTabLoading(true);
    const r = await contactsService.getTimeline(id, t);
    setData(r.success ? r.data : null);
    if (!r.success) setError(r.error || 'Could not load this channel');
    setTabLoading(false);
  }, [id]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const addRemark = async () => {
    if (!id || !remark.trim()) return;
    setSaving(true);
    const r = await contactsService.addNote(id, remark.trim());
    setSaving(false);
    if (!r.success) { setError(r.error || 'Could not save the remark'); return; }
    setRemark('');
    loadTab('notes');
  };

  if (loading) return <LoadingSpinner />;
  if (!contact) return <div className="p-6"><ErrorMessage message={error || 'Contact not found'} /></div>;

  const events: any[] = data?.events || [];
  // The transform in contacts.service renames everything to snake_case:
  // phone_numbers[].number, email_addresses[].email, labels[]. Reading the API's
  // own camelCase here renders "None on file" for every contact and errors nowhere.
  const phones: any[] = contact.phone_numbers || [];
  const emails: any[] = contact.email_addresses || [];
  const labels: string[] = contact.labels || [];
  const custom: Record<string, any> = (() => {
    const raw = contact.custom_fields;
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return {}; }
  })();

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <button onClick={() => navigate('/contacts')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Contacts
      </button>

      {/* Identity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {contact.name || contact.first_name || phones[0]?.number || 'Unnamed contact'}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
          {contact.designation && (
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{contact.designation}</span>
          )}
          {contact.company && (
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{contact.company}</span>
          )}
          {contact.source && <span className="text-xs">via {contact.source}</span>}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Phone</div>
            {phones.length === 0 && <p className="text-gray-400">None on file</p>}
            {phones.map((p: any, i: number) => (
              <div key={i} className="text-gray-800 dark:text-gray-200">
                {p.number}
                {p.type && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">{p.type}</span>}
                {p.is_primary && <span className="ml-1 text-[10px] text-indigo-500">primary</span>}
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Email</div>
            {emails.length === 0 && <p className="text-gray-400">None on file</p>}
            {emails.map((e: any, i: number) => (
              <div key={i} className="text-gray-800 dark:text-gray-200 break-all">
                {e.email}
                {e.type && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">{e.type}</span>}
                {e.is_primary && <span className="ml-1 text-[10px] text-indigo-500">primary</span>}
              </div>
            ))}
          </div>
        </div>

        {labels.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {labels.map((t) => (
              <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Record metadata. "First seen" rather than "created": the row is created
            when we first hear from somebody, which is what the date actually means
            to whoever is reading it. */}
        <dl className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">First seen</dt>
            <dd className="text-gray-800 dark:text-gray-200">{when(contact.created_at) || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Last updated</dt>
            <dd className="text-gray-800 dark:text-gray-200">{when(contact.updated_at) || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Last contacted</dt>
            {/* Blank is meaningful here and is left blank rather than filled with
                "never" — the field is only set by paths that record it, so an empty
                value means "not recorded", not "we never spoke to them". */}
            <dd className="text-gray-800 dark:text-gray-200">{when(contact.last_contact_at) || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Status</dt>
            <dd className="text-gray-800 dark:text-gray-200">
              {contact.status || '—'}
              {contact.assignedTo && (
                <span className="block text-xs text-gray-500">assigned to {contact.assignedTo}</span>
              )}
            </dd>
          </div>
        </dl>

        {Object.keys(custom).length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Captured details</div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {Object.entries(custom).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="text-gray-400 shrink-0">{k.replace(/_/g, ' ')}</dt>
                  <dd className="text-gray-800 dark:text-gray-200 break-words">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {contact.notes && String(contact.notes).trim() !== '' && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {/* Labelled distinctly from Remarks. This is the single overwritable
                field on the contact record; the Remarks tab is the dated, attributed
                sequence. Showing both as "notes" would suggest one history. */}
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Contact record note</div>
            <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{contact.notes}</p>
          </div>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Channel tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-gray-100 dark:border-gray-700">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'}`}>
              {t.icon} {t.label}
              {/* Shown rather than hidden: a missing tab reads as "no such channel",
                  and the point is that it is coming. */}
              {t.soon && <span className="text-[10px] px-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">soon</span>}
            </button>
          ))}
        </div>

        <div className="p-4">
          {tabLoading && <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>}

          {!tabLoading && data && data.supported === false && (
            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300 py-6">
              <Clock className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
              <p>{data.note}</p>
            </div>
          )}

          {/* A short list because a switch was down looks exactly like a quiet
              customer, so the difference is stated. */}
          {!tabLoading && data?.partial && (
            <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 mb-3 bg-amber-50 text-amber-800 border border-amber-200
                            dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{data.note}</span>
            </div>
          )}

          {tab === 'calls' && id && (
            <ContactCalls contactId={id} phones={phones.map((p: any) => p.number).filter(Boolean)} />
          )}

          {tab === 'notes' && (
            <div className="mb-4 flex flex-wrap items-start gap-2">
              <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
                placeholder="What happened? e.g. called about pricing, wants a quote by Friday"
                className="flex-1 min-w-[18rem] px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
              <button onClick={addRemark} disabled={saving || !remark.trim()}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Add remark'}
              </button>
            </div>
          )}

          {tab !== 'calls' && !tabLoading && data?.supported !== false && events.length === 0 && (
            <p className="text-sm text-gray-400 py-6 text-center">
              {data?.note || 'Nothing on this channel yet.'}
            </p>
          )}

          {/* Every channel but calls. Calls are rendered above by ContactCalls,
              which opens them; listing them here as well would put a second,
              unclickable copy of the same calls under the working one. */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {(tab === 'calls' ? [] : events).map((e, i) => (
              <div key={e.ref || i} className="py-3 flex gap-3">
                <div className="w-36 shrink-0 text-xs text-gray-400">{when(e.at)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs mb-0.5">
                    {e.direction && (
                      <span className={`px-1.5 py-0.5 rounded ${e.direction === 'OUTBOUND'
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {e.direction === 'OUTBOUND' ? 'sent' : 'received'}
                      </span>
                    )}
                    {e.status && <span className="text-gray-400">{e.status}</span>}
                    {e.templateName && <span className="text-gray-400">{e.templateName}</span>}
                    {e.source && <span className="text-gray-400">{e.source}</span>}
                    {e.authorName && <span className="text-gray-500">{e.authorName}</span>}
                  </div>

                  {e.subject && <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{e.subject}</div>}
                  {e.body && (
                    <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{e.body}</p>
                  )}
                  {e.channel === 'calls' && (
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                      {e.from} → {e.to}
                      {e.durationSeconds != null && <span className="text-gray-400"> · {e.durationSeconds}s</span>}
                      {e.hasTranscript && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400">
                          <FileText className="w-3 h-3" /> transcript
                        </span>
                      )}
                    </p>
                  )}
                  {e.errorMessage && <p className="text-xs text-red-600 mt-0.5">{e.errorMessage}</p>}
                  {e.errorCode && <p className="text-xs text-red-600 mt-0.5">{e.errorCode}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* What the answer was built from. Without it a thin timeline is
              unexplainable — "no email on file" and "never emailed" look the same. */}
          {!tabLoading && data?.supported !== false && (data?.matchedPhones?.length > 0 || data?.matchedEmails?.length > 0) && (
            <p className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-400">
              Matched on {[...(data.matchedPhones || []), ...(data.matchedEmails || [])].join(', ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactProfile;
